import {
  readConfig,
  getCustomerKey,
  getSessionKey,
  getTouchpoint,
  createProductCardRenderer,
  buildSwatchStyle,
  syncCustomerKey,
  fetchAddToCartPopup,
  escapeHtml,
  escapeAttr,
  formatPrice,
  safeUrl,
} from 'voyado-elevate';

const _syncOnce = syncCustomerKey();

const RECOMMENDATION_LABELS = {
  ALTERNATIVES: 'Ähnliche Produkte',
  UPSELL: 'Das könnte Ihnen auch gefallen',
  STYLE_WITH: 'Dazu passt',
  PERSONAL: 'Persönliche Empfehlungen',
  TOP_PRODUCTS: 'Top-Produkte',
  NEWEST_PRODUCTS: 'Neu eingetroffen',
  MORE_FROM_SERIES: 'Mehr aus der Serie',
  RECENTLY_VIEWED: 'Zuletzt angesehen',
  FAVORITES: 'Favoriten',
  CART: 'Passend zu Ihrem Warenkorb',
  ADD_TO_CART_RECS: 'Weitere Empfehlungen',
};

function splitBr0003Title(title) {
  const fullTitle = String(title || '').trim();
  if (!fullTitle) return { title: '', subtitle: '' };

  const words = fullTitle.split(/\s+/).filter(Boolean);
  let primaryTitle = words[0] || '';

  if (words[0] === 'GOLF' && (words[1] === 'SECURE' || words[1] === '2.0')) {
    primaryTitle = `${words[0]} ${words[1]}`;
  }

  let subtitle = fullTitle.slice(primaryTitle.length).trim();
  if (subtitle.startsWith('RFID ')) {
    subtitle = subtitle.slice(5).trim();
  } else if (subtitle === 'RFID') {
    subtitle = '';
  }

  return {
    title: primaryTitle || fullTitle,
    subtitle,
  };
}

function readBr0003Registry() {
  const script = document.querySelector('[data-voyado-atc-title-registry]');
  if (!(script instanceof HTMLScriptElement)) return new Map();

  try {
    const payload = JSON.parse(script.textContent || '{}');
    const entries = Array.isArray(payload?.products) ? payload.products : [];
    return new Map(
      entries
        .map((entry) => {
          const ids = Array.isArray(entry?.ids) ? entry.ids.map((id) => String(id || '').trim()).filter(Boolean) : [];
          if (ids.length === 0) return null;
          return [ids, {
            title: String(entry?.title || '').trim(),
            subtitle: String(entry?.subtitle || '').trim(),
          }];
        })
        .filter(Boolean)
        .flatMap(([ids, value]) => ids.map((id) => [id, value]))
    );
  } catch {
    return new Map();
  }
}

function readBr0003TitleFromSource(eventTarget) {
  if (!(eventTarget instanceof Element)) return null;

  const titleContainer = eventTarget.closest('[data-voyado-card], .product-card, .quick-buy-modal__content, .product-info');
  if (!titleContainer) return null;

  const titleText = titleContainer.querySelector('[data-br-0003-title], .voyado-atc-popup__product-title, .product-title, .voyado-search__product-title, .voyado-autocomplete__product-title')?.textContent?.trim() || '';
  const subtitleText = titleContainer.querySelector('[data-br-0003-subtitle], .product-card__subtitle, .voyado-autocomplete__product-subtitle, .voyado-atc-popup__product-variant')?.textContent?.trim() || '';

  if (!titleText) return null;
  return { title: titleText, subtitle: subtitleText };
}

function resolveAddedProductTexts(item, eventTarget, titleRegistry) {
  const productId = String(item?.product_id ?? '').trim();
  const variantId = String(item?.variant_id ?? item?.id ?? '').trim();
  const registryMatch = titleRegistry.get(variantId) || titleRegistry.get(productId);
  if (registryMatch?.title) return registryMatch;

  const sourceMatch = readBr0003TitleFromSource(eventTarget);
  if (sourceMatch?.title) return sourceMatch;

  const productTitle = String(item?.product_title || item?.title || '').trim();
  return splitBr0003Title(productTitle);
}

function readPopupConfig(dialog) {
  const data = dialog.dataset;

  return {
    locale: data.voyadoLocale || '',
    algorithm: data.algorithm || 'ADD_TO_CART_RECS',
    limit: parseInt(data.limit || '4', 10) || 4,
    columnsDesktop: parseInt(data.columnsDesktop || '4', 10) || 4,
    columnsMobile: parseInt(data.columnsMobile || '2', 10) || 2,
    showImage: data.showImage !== 'false',
    imageAspectRatio: data.imageAspectRatio || 'portrait',
    showPrice: data.showPrice !== 'false',
    showSwatch: false,
    headingLevel: 'h3',
    priceId: data.priceId || '',
    stores: data.stores || '',
    presentCustom: data.presentCustom || '',
    successMessage: data.successMessage || 'Zum Warenkorb hinzugefügt',
  };
}

function buildCartItems(cartItems) {
  return Array.isArray(cartItems)
    ? cartItems
      .map((item) => {
        const itemKey = String(item?.variant_id ?? item?.id ?? '').trim();
        const quantity = parseInt(String(item?.quantity ?? ''), 10);

        if (!itemKey || !Number.isFinite(quantity) || quantity <= 0) return null;
        return { itemKey, quantity };
      })
      .filter(Boolean)
    : [];
}

function formatShopifyPrice(cents, locale) {
  const amount = Number(cents);
  return Number.isFinite(amount) ? formatPrice(amount / 100, locale) : '';
}

function normalizeAddedItem(item) {
  const variantKey = String(item?.sku ?? '').trim();
  const quantity = parseInt(String(item?.quantity ?? ''), 10);

  if (!variantKey || !Number.isFinite(quantity) || quantity <= 0) return null;
  return { variantKey, quantity };
}

function renderAddedProduct(item, config, eventTarget, titleRegistry) {
  const resolvedTitle = resolveAddedProductTexts(item, eventTarget, titleRegistry);
  const productTitle = resolvedTitle.title || String(item?.product_title || item?.title || '').trim();
  const productSubtitle = resolvedTitle.subtitle || '';
  const variantTitle = String(item?.title || '').trim();
  const imageUrl = safeUrl(item?.featured_image?.url || '');
  const productUrl = safeUrl(item?.url || '#');
  const price = formatShopifyPrice(item?.price, config.locale);
  const hasVariantTitle = !productSubtitle && variantTitle && variantTitle !== productTitle && variantTitle.toLowerCase() !== 'default title';
  const imageHtml = imageUrl !== '#'
    ? `<img src="${escapeAttr(imageUrl)}" alt="${escapeAttr(productTitle)}" loading="lazy" width="80" height="80">`
    : '<div class="voyado-search__image-placeholder"></div>';

  return `
    <a href="${escapeAttr(productUrl)}" class="voyado-atc-popup__product-card">
      <div class="voyado-atc-popup__product-image">${imageHtml}</div>
      <div class="voyado-atc-popup__product-info">
        <p class="voyado-atc-popup__product-title">${escapeHtml(productTitle)}</p>
        ${productSubtitle ? `<p class="voyado-atc-popup__product-variant">${escapeHtml(productSubtitle)}</p>` : ''}
        ${hasVariantTitle ? `<p class="voyado-atc-popup__product-variant">${escapeHtml(variantTitle)}</p>` : ''}
        ${price ? `<p class="voyado-atc-popup__product-price">${escapeHtml(price)}</p>` : ''}
      </div>
    </a>
  `;
}

class VoyadoAtcPopup {
  constructor(dialog) {
    this._dialog = dialog;
    this._config = readPopupConfig(dialog);
    this._baseConfig = readConfig();
    Object.assign(this, createProductCardRenderer(() => this._config, {
      buildSwatchStyleFn: buildSwatchStyle,
      productInfoVariant: 'product',
    }));
    this._renderProductSwatches = () => '';
    this._initSliderForCard = () => {};
    this._productSlot = dialog.querySelector('#voyado-atc-product');
    this._recsSlot = dialog.querySelector('#voyado-atc-recs');
    this._abortController = null;
    this._requestId = 0;
    this._titleRegistry = readBr0003Registry();

    this._bindEvents();
    this._bindProductCardContainer(this._recsSlot);
  }

  show(variantAddDetail, eventTarget) {
    const item = Array.isArray(variantAddDetail?.items) ? variantAddDetail.items[0] : null;
    if (!item || !this._productSlot || !this._recsSlot) return;

    this._productSlot.innerHTML = renderAddedProduct(item, this._config, eventTarget, this._titleRegistry);
    this._recsSlot.hidden = true;
    this._recsSlot.innerHTML = '';

    if (!this._dialog.open) {
      this._dialog.showModal();
    }

    const addedItem = normalizeAddedItem(item);
    if (addedItem) {
      this._fetchAndRenderRecs(buildCartItems(variantAddDetail?.cart?.items), addedItem);
    }
  }

  async _fetchAndRenderRecs(cartItems, addedItem) {
    await _syncOnce;
    this._abortController?.abort();
    this._abortController = new AbortController();
    const requestId = ++this._requestId;
    const urlParams = {
      customerKey: getCustomerKey(),
      sessionKey: getSessionKey(),
      market: this._baseConfig.market,
      locale: this._config.locale || this._baseConfig.locale,
      touchpoint: getTouchpoint(),
      variantKey: addedItem.variantKey,
    };

    if (this._config.priceId) urlParams.priceId = this._config.priceId;
    if (this._config.stores) urlParams.stores = this._config.stores;
    if (this._config.presentCustom) urlParams.presentCustom = this._config.presentCustom;

    const body = {
      ...(cartItems.length > 0 ? { cart: cartItems } : {}),
      recommendationLists: [{
        id: 'list-1',
        algorithm: this._config.algorithm,
        label: this._getRecommendationLabel(this._config.algorithm),
        limit: this._config.limit,
        visualization: 'GRID',
      }],
    };

    try {
      const data = await fetchAddToCartPopup(
        this._baseConfig.cluster,
        urlParams,
        body,
        this._abortController.signal
      );
      if (requestId !== this._requestId) return;

      this._renderRecs(Array.isArray(data?.recommendationLists) ? data.recommendationLists : []);
    } catch {
      if (requestId === this._requestId && this._recsSlot) {
        this._recsSlot.hidden = true;
        this._recsSlot.innerHTML = '';
      }
    }
  }

  _renderRecs(lists) {
    if (!this._recsSlot) return false;

    const sectionsHtml = (Array.isArray(lists) ? lists : [])
      .map((list) => {
        if (list?.visible === false) return '';

        const productGroups = this._normalizeProductGroups(list?.productGroups ?? list?.products ?? []);
        if (productGroups.length === 0) return '';

        const cardsHtml = productGroups.map((productGroup) => this._renderProductCard(productGroup)).filter(Boolean).join('');
        if (!cardsHtml) return '';

        const listName = String(list?.label ?? list?.id ?? '').trim();
        return `
          <section class="voyado-rec__list">
            ${listName ? `<div class="prose prose--tight"><h3 class="h5">${escapeHtml(listName)}</h3></div>` : ''}
            ${this._renderGridInner(cardsHtml)}
          </section>
        `;
      })
      .filter(Boolean)
      .join('');

    this._recsSlot.hidden = !sectionsHtml;
    this._recsSlot.innerHTML = sectionsHtml;
    return Boolean(sectionsHtml);
  }

  _bindEvents() {
    this._dialog.querySelectorAll('[data-atc-popup-close]').forEach((el) => {
      el.addEventListener('click', () => this._hide());
    });
    this._dialog.addEventListener('click', (event) => {
      if (event.target === this._dialog) {
        this._hide();
      }
    });
  }

  _hide() {
    this._abortController?.abort();
    this._abortController = null;
    if (this._dialog.open) {
      this._dialog.close();
    }
  }

  _renderGridInner(cardsHtml) {
    return `<div class="voyado-search__grid" style="--columns-desktop:${escapeAttr(String(this._config.columnsDesktop))};--columns-mobile:${escapeAttr(String(this._config.columnsMobile))}">${cardsHtml}</div>`;
  }

  _canRenderSwatches() {
    return this._config.showSwatch && this._baseConfig.showSwatches;
  }

  _getRecommendationLabel(algorithm) {
    const key = String(algorithm || '').trim().toUpperCase();
    return RECOMMENDATION_LABELS[key] || 'Empfehlungen';
  }
}

function initializeVoyadoAtcPopup() {
  const dialog = document.getElementById('voyado-atc-popup');
  if (!(dialog instanceof HTMLDialogElement)) return;

  try {
    const controller = new VoyadoAtcPopup(dialog);
    document.documentElement.addEventListener('variant:add', (event) => {
      if (window.themeVariables?.settings?.cartType !== 'message') return;
      controller.show(event.detail, event.target);
    });
  } catch {}
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeVoyadoAtcPopup, { once: true });
} else {
  initializeVoyadoAtcPopup();
}
